import { configureStore } from '@reduxjs/toolkit'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'

import { agentApi } from './api/agentApi'
import { applicationApi } from './api/applicationApi'
import { authApi } from './api/authApi'
import { bounceApi } from './api/bounceApi'
import { clientApi } from './api/clientApi'

import { SaleIntegrityApi } from './api/SaleIntegrityApi'
import { dateTriggerTypeApi } from './api/dateTriggerTypeApi'
import { dealerGroupAPi } from './api/dealerGroupApi'
import { dealerGroupNumberApi } from './api/dealergroupnumberApi'
import { dealerTypeApi } from './api/dealerType'
import { dispatchMessageApi } from './api/dispatchMessageMasterApi'
import { dispatchStatusApi } from './api/dispatchStatusApi'
import { flightTrackerConsumerStatusApi } from './api/flightTrackerConsumerStatusApi'
import { flightTrackerDealerApi } from './api/flightTrackerDealerApi'
import { flightTrackerServicerApi } from './api/flightTrackerServicerApi'
import { leadTypeApi } from './api/leadTypeApi'
import { marketingTypeApi } from './api/marketingTypeApi'
import { moduleApi } from './api/moduleApi'
import { productStatusApi } from './api/productStatusApi'
import { roleApi } from './api/roleApi'
import { saleSourceApi } from './api/saleSourceApi'
import { solicitationPurchaseIncompleteApi } from './api/reports/solicitationPurchaseIncompleteApi'
import { solicitationApi } from './api/solicitationApi'
import { surveyMonkeyApi } from './api/surveyMonkeyMasterApi'
import { triggerFactorApi } from './api/triggerFactorApi'
import { userApi } from './api/userApi'

import applicationReducer from './slice/applicationSlice'
import authReducer from './slice/authSlice'
import dateTriggerTypeReducer from './slice/dateTriggerTypeSlice'
import dealerGroupReducer from './slice/dealerGroupSlice'
import dealerTypeReducer from './slice/dealerTypeSlice'
import dispatchMessageReducer from './slice/dispatchMessageSlice'
import flightTrackerConsumerStatusReducer from './slice/flightTrackerConsumerStatusSlice'
import flightTrackerDealerReducer from './slice/flightTrackerDealerSlice'
import flightTrackerServicerReducer from './slice/flightTrackerServicerSlice'
import leadTypeReducer from './slice/leadTypeSlice'
import marketingTypeReducer from './slice/marketingTypeSlice'
import surveyMonkeyReducer from './slice/surveyMonkeySlice'
import triggerFactorReducer from './slice/triggerFactorSlice'
import userReducer from './slice/userSlice'
import yearDetailsReducer from './slice/yearDetailsSlice'

export const store = configureStore({
  reducer: {
    [agentApi.reducerPath]: agentApi.reducer,
    [applicationApi.reducerPath]: applicationApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [bounceApi.reducerPath]: bounceApi.reducer,
    [clientApi.reducerPath]: clientApi.reducer,

    [SaleIntegrityApi.reducerPath]: SaleIntegrityApi.reducer,

    [dateTriggerTypeApi.reducerPath]: dateTriggerTypeApi.reducer,
    [dealerGroupAPi.reducerPath]: dealerGroupAPi.reducer,
    [dealerGroupNumberApi.reducerPath]: dealerGroupNumberApi.reducer,
    [dealerTypeApi.reducerPath]: dealerTypeApi.reducer,
    [dispatchMessageApi.reducerPath]: dispatchMessageApi.reducer,
    [dispatchStatusApi.reducerPath]: dispatchStatusApi.reducer,
    [flightTrackerConsumerStatusApi.reducerPath]: flightTrackerConsumerStatusApi.reducer,
    [flightTrackerDealerApi.reducerPath]: flightTrackerDealerApi.reducer,
    [flightTrackerServicerApi.reducerPath]: flightTrackerServicerApi.reducer,
    [leadTypeApi.reducerPath]: leadTypeApi.reducer,
    [marketingTypeApi.reducerPath]: marketingTypeApi.reducer,
    [moduleApi.reducerPath]: moduleApi.reducer,
    [productStatusApi.reducerPath]: productStatusApi.reducer,
    [roleApi.reducerPath]: roleApi.reducer,
    [saleSourceApi.reducerPath]: saleSourceApi.reducer,
    [solicitationApi.reducerPath]: solicitationApi.reducer,
    [solicitationPurchaseIncompleteApi.reducerPath]: solicitationPurchaseIncompleteApi.reducer,
    [surveyMonkeyApi.reducerPath]: surveyMonkeyApi.reducer,
    [triggerFactorApi.reducerPath]: triggerFactorApi.reducer,
    [userApi.reducerPath]: userApi.reducer,

    applicationState: applicationReducer,
    authState: authReducer,
    dateTriggerTypeState: dateTriggerTypeReducer,
    dealerGroupState: dealerGroupReducer,
    dealerTypeState: dealerTypeReducer,
    dispatchMessageState: dispatchMessageReducer,
    flightTrackerConsumerStatusState: flightTrackerConsumerStatusReducer,
    flightTrackerDealerState: flightTrackerDealerReducer,
    flightTrackerServicerState: flightTrackerServicerReducer,
    leadTypeState: leadTypeReducer,
    marketingTypeState: marketingTypeReducer,
    surveyMonkeyState: surveyMonkeyReducer,
    triggerFactorState: triggerFactorReducer,
    userState: userReducer,
    yearDetailSate: yearDetailsReducer,
  },
  devTools: process.env.NODE_ENV === 'development',
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({}).concat([
      agentApi.middleware,
      applicationApi.middleware,
      authApi.middleware,
      bounceApi.middleware,
      clientApi.middleware,

      SaleIntegrityApi.middleware,

      dateTriggerTypeApi.middleware,
      dealerGroupAPi.middleware,
      dealerGroupNumberApi.middleware,
      dealerTypeApi.middleware,
      dispatchMessageApi.middleware,
      dispatchStatusApi.middleware,
      flightTrackerConsumerStatusApi.middleware,
      flightTrackerDealerApi.middleware,
      flightTrackerServicerApi.middleware,
      leadTypeApi.middleware,
      marketingTypeApi.middleware,
      moduleApi.middleware,
      productStatusApi.middleware,
      roleApi.middleware,
      saleSourceApi.middleware,
      solicitationApi.middleware,
      solicitationPurchaseIncompleteApi.middleware,
      surveyMonkeyApi.middleware,
      triggerFactorApi.middleware,
      userApi.middleware,
    ]),
})

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector
